import React from "react";
import { LegalLayout, LegalSection } from "./LegalLayout";

export default function TermsPage() {
  return (
    <LegalLayout
      current="/conditions"
      title="Conditions d'utilisation"
      subtitle="Les présentes conditions régissent l'accès et l'utilisation de la plateforme PROGÈRE et de ses modules."
      updated="Juillet 2026"
    >
      <LegalSection id="acceptation" title="1. Acceptation des conditions">
        <p>
          En accédant à la plateforme PROGÈRE, exploitée par <strong className="text-white">Groupe MJR Construction Inc.</strong>,
          ou en l'utilisant, vous acceptez d'être lié par les présentes conditions d'utilisation. Si vous n'acceptez pas ces conditions,
          veuillez ne pas utiliser nos services.
        </p>
      </LegalSection>

      <LegalSection id="services" title="2. Description des services">
        <p>
          PROGÈRE est un écosystème logiciel infonuagique (SaaS) destiné aux entreprises de construction, regroupant plusieurs modules
          (PUNCH, CRM, ERP, COMPTA, DEVIS AI et leurs options) accessibles selon l'abonnement souscrit.
        </p>
      </LegalSection>

      <LegalSection id="compte" title="3. Compte et sécurité">
        <p>
          Vous êtes responsable de la confidentialité de vos identifiants et de toutes les activités effectuées à partir de votre compte.
          Vous vous engagez à fournir des renseignements exacts et à les tenir à jour.
        </p>
      </LegalSection>

      <LegalSection id="abonnement" title="4. Abonnement, prix et taxes">
        <ul className="list-disc pl-5 space-y-1.5">
          <li>Les abonnements sont facturés mensuellement, en dollars canadiens (CAD).</li>
          <li>Les prix affichés s'entendent avant taxes; la TPS et la TVQ applicables sont ajoutées au moment du paiement.</li>
          <li>Les rabais multi-modules sont appliqués automatiquement selon le nombre de modules actifs.</li>
          <li>Le paiement est traité de façon sécurisée par Stripe. Les abonnements se renouvellent automatiquement jusqu'à annulation.</li>
        </ul>
      </LegalSection>

      <LegalSection id="annulation" title="5. Annulation et remboursement">
        <p>
          Vous pouvez annuler votre abonnement en tout temps; l'accès demeure actif jusqu'à la fin de la période payée en cours.
          Sauf disposition contraire prévue par la loi, les montants déjà facturés ne sont pas remboursables.
        </p>
      </LegalSection>

      <LegalSection id="usage" title="6. Utilisation acceptable">
        <p>Vous vous engagez à ne pas&nbsp;:</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>Utiliser les services à des fins illégales ou non autorisées.</li>
          <li>Tenter d'accéder sans autorisation aux systèmes ou aux données d'autrui.</li>
          <li>Perturber, surcharger ou compromettre l'intégrité de la plateforme.</li>
        </ul>
      </LegalSection>

      <LegalSection id="propriete" title="7. Propriété intellectuelle">
        <p>
          La plateforme, ses modules, sa marque et son contenu sont la propriété de Groupe MJR Construction Inc. ou de ses concédants.
          Vos données d'affaires demeurent votre propriété; vous nous accordez uniquement les droits nécessaires à la fourniture du service.
        </p>
      </LegalSection>

      <LegalSection id="disponibilite" title="8. Disponibilité et support">
        <p>
          Nous mettons des efforts raisonnables pour assurer la disponibilité du service, sans garantie d'absence d'interruption.
          Le module PROGÈRE SUPPORT est inclus dans tous les abonnements.
        </p>
      </LegalSection>

      <LegalSection id="responsabilite" title="9. Limitation de responsabilité">
        <p>
          Dans les limites permises par la loi, PROGÈRE ne saurait être tenue responsable des dommages indirects, accessoires ou
          consécutifs découlant de l'utilisation ou de l'impossibilité d'utiliser les services.
        </p>
      </LegalSection>

      <LegalSection id="droit-applicable" title="10. Droit applicable">
        <p>
          Les présentes conditions sont régies par les lois de la province de Québec et les lois du Canada qui y sont applicables.
          Tout litige sera soumis aux tribunaux compétents du district judiciaire concerné, au Québec.
        </p>
      </LegalSection>

      <LegalSection id="modifications" title="11. Modifications">
        <p>
          Nous pouvons modifier les présentes conditions. Les changements importants vous seront communiqués. L'utilisation continue
          des services après la mise à jour vaut acceptation des nouvelles conditions.
        </p>
      </LegalSection>

      <LegalSection id="definitions" title="12. Définition des parties">
        <p>
          <strong className="text-white">« PROGÈRE » ou « Fournisseur »</strong> : Groupe MJR Construction inc., uniquement à titre
          d'exploitant, de fournisseur technologique et de concédant de licence de la plateforme PROGÈRE.
        </p>
        <p>
          <strong className="text-white">« Client »</strong> : l'entreprise, l'organisation ou le travailleur autonome qui souscrit à un
          abonnement PROGÈRE.
        </p>
        <p>
          <strong className="text-white">« Utilisateur autorisé »</strong> : toute personne à qui le Client donne accès à la plateforme,
          notamment un propriétaire, administrateur, employé, contremaître, chargé de projet, comptable, sous-traitant, fournisseur ou
          client invité.
        </p>
      </LegalSection>

      <LegalSection id="responsabilite-client" title="13. Responsabilité générale du Client">
        <p>
          Le Client reconnaît qu'il demeure seul responsable de la gestion, de l'administration et de l'utilisation de son entreprise
          ainsi que de toutes les décisions prises à partir des informations enregistrées, calculées, importées, exportées ou présentées
          dans PROGÈRE.
        </p>
        <p>PROGÈRE constitue un outil technologique d'aide à la gestion et ne remplace notamment pas&nbsp;:</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>un comptable; un fiscaliste; un avocat; un conseiller juridique;</li>
          <li>un professionnel de la paie; un professionnel de la santé et sécurité;</li>
          <li>un ingénieur; un architecte; un estimateur qualifié;</li>
          <li>un responsable des ressources humaines; un conseiller financier; une autorité gouvernementale.</li>
        </ul>
        <p>
          Le Client doit vérifier et approuver les renseignements avant de les utiliser pour prendre une décision, produire une
          déclaration, effectuer un paiement ou transmettre un document à une personne, un client, un employé, un fournisseur ou une
          autorité gouvernementale.
        </p>
      </LegalSection>

      <LegalSection id="responsabilite-acces" title="14. Responsabilité des utilisateurs et des accès">
        <p>Le Client est responsable&nbsp;:</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>de choisir ses utilisateurs autorisés et de leur attribuer les bons rôles et permissions;</li>
          <li>de retirer rapidement les accès devenus inutiles;</li>
          <li>de protéger les mots de passe et les appareils et d'activer l'authentification à deux facteurs lorsque disponible;</li>
          <li>de vérifier les accès administratifs et de surveiller les activités effectuées sous ses comptes;</li>
          <li>d'informer PROGÈRE de tout accès suspect;</li>
          <li>des actions effectuées par ses utilisateurs autorisés et des informations transmises par ses employés et sous-traitants.</li>
        </ul>
        <p>
          Toute action effectuée à partir d'un compte valablement authentifié est présumée avoir été autorisée par le Client, sous
          réserve d'une preuve d'accès non autorisé ou d'une défaillance imputable au Fournisseur. Le Client ne doit pas partager un
          compte entre plusieurs personnes lorsqu'un compte individuel est requis.
        </p>
      </LegalSection>

      <LegalSection id="responsabilite-donnees" title="15. Responsabilité relative aux données">
        <p>
          Le Client demeure responsable de la légalité, de l'exactitude, de la qualité et de la mise à jour des données qu'il saisit ou
          téléverse (documents, photos, signatures, coordonnées, heures de travail, taux, prix, taxes, codes comptables, informations de
          paie, renseignements relatifs à ses employés et à ses clients), ainsi que des consentements qu'il doit obtenir et des avis
          qu'il doit donner aux personnes concernées.
        </p>
        <p>
          Le Client garantit qu'il possède les droits, autorisations et consentements nécessaires pour recueillir, utiliser, enregistrer,
          téléverser et communiquer les données qu'il place dans PROGÈRE. Le Client ne doit pas utiliser PROGÈRE pour enregistrer des
          données qu'il n'a pas le droit de recueillir ou de traiter.
        </p>
      </LegalSection>

      <LegalSection id="renseignements-personnels" title="16. Renseignements personnels">
        <p>
          Pour les renseignements personnels que le Client recueille concernant ses employés, candidats, clients, fournisseurs ou autres
          personnes dans le cadre de ses propres activités, le Client agit comme organisation responsable de la collecte et de
          l'utilisation de ces renseignements. Il est notamment responsable d'identifier les finalités, de limiter la collecte, de fournir
          les avis requis, d'obtenir les consentements requis, de traiter les demandes d'accès et de rectification, de définir ses règles
          de conservation et de configurer adéquatement les permissions.
        </p>
        <p>
          PROGÈRE agit comme fournisseur technologique et prestataire de services pour les données traitées pour le compte du Client, sauf
          pour les données que PROGÈRE recueille pour ses propres besoins (gestion des comptes, abonnements, sécurité, facturation et
          support). Groupe MJR Construction inc. conserve néanmoins ses propres obligations légales relativement aux renseignements
          personnels qu'elle recueille, détient, utilise, communique ou conserve dans le cadre de l'exploitation de PROGÈRE.
        </p>
      </LegalSection>

      <LegalSection id="paie-ccq" title="17. Paie, CCQ et ressources humaines (PUNCH, PAIE)">
        <p>
          Le Client est seul responsable de vérifier et d'approuver les heures régulières et supplémentaires, pauses, primes, allocations,
          absences, vacances, classifications, métiers, taux, conventions, règles CCQ, déductions, avantages imposables, informations
          transmises à la paie et déclarations gouvernementales.
        </p>
        <p>
          PROGÈRE peut automatiser la préparation, le classement, les calculs préliminaires et les exportations, mais ne garantit pas
          qu'une donnée entrée incorrectement produira un résultat conforme. Aucune paie ni déclaration ne doit être considérée approuvée
          uniquement parce qu'elle a été générée par PROGÈRE&nbsp;: une approbation humaine autorisée doit être exigée avant toute
          transmission finale.
        </p>
      </LegalSection>

      <LegalSection id="comptabilite" title="18. Comptabilité, taxes et finances (COMPTA, ERP, DEVIS AI)">
        <p>
          Le Client demeure seul responsable de ses livres comptables, taxes, déclarations, factures, crédits, retenues, dépenses,
          revenus, comptes clients et fournisseurs, budgets, prévisions et décisions financières, ainsi que de la vérification des
          résultats.
        </p>
        <p>
          Les calculs et rapports fournis par PROGÈRE sont fondés sur les informations fournies par le Client et doivent être vérifiés par
          une personne compétente. PROGÈRE ne fournit pas de conseil fiscal, comptable, juridique ou financier.
        </p>
      </LegalSection>

      <LegalSection id="devis-ia" title="19. Devis, Take Off et intelligence artificielle">
        <p>
          Les quantités, estimations, descriptions, prix, analyses et contenus produits ou suggérés par PROGÈRE peuvent comporter des
          erreurs, des omissions ou des interprétations incomplètes. Le Client doit vérifier notamment les plans, dimensions, échelles,
          quantités, matériaux, heures, taux, prix, inclusions, exclusions, conditions, marges, exigences contractuelles et les lois et
          normes applicables.
        </p>
        <p>
          Aucune soumission, commande, directive, facture, déclaration ou décision ne doit être envoyée ou exécutée automatiquement sans
          validation humaine. Là où l'intelligence artificielle intervient, la mention suivante est affichée&nbsp;:
          <em className="text-[#EAB308]"> « Contenu préparé avec PROGÈRE AI — validation humaine requise. »</em>
        </p>
      </LegalSection>

      <LegalSection id="geolocalisation" title="20. Géolocalisation et surveillance des employés">
        <p>
          Le Client est responsable de déterminer si l'utilisation de la géolocalisation, du pointage, des photos, des journaux
          d'activité et des fonctions de surveillance est légale, nécessaire et proportionnée dans son contexte. Il est responsable
          d'informer ses employés, d'obtenir les consentements requis lorsque nécessaire, d'adopter une politique interne, de limiter
          l'accès, de choisir les réglages appropriés et de respecter les lois du travail et les conventions applicables.
        </p>
        <p>
          PROGÈRE fournit les réglages technologiques, mais ne détermine pas à la place du Client si une mesure de surveillance est
          juridiquement permise.
        </p>
      </LegalSection>

      <LegalSection id="fournisseurs-tiers" title="21. Fournisseurs tiers et intégrations">
        <p>
          PROGÈRE peut dépendre de services tiers (hébergement, bases de données, paiements, courriels, cartes, notifications,
          intelligence artificielle, intégrations comptables, stockage, surveillance des erreurs). PROGÈRE n'est pas responsable des actes
          indépendants d'un fournisseur tiers qui échappent à son contrôle raisonnable, sous réserve de ses obligations de sélectionner et
          d'encadrer raisonnablement ses fournisseurs et de respecter les lois applicables. Le Client demeure responsable de ses comptes
          et contrats directs avec les fournisseurs tiers qu'il choisit de connecter à PROGÈRE.
        </p>
      </LegalSection>

      <LegalSection id="limitation-responsabilite" title="22. Limitation de responsabilité">
        <p>
          Dans toute la mesure permise par les lois applicables, Groupe MJR Construction inc., ses administrateurs, dirigeants, employés,
          mandataires et fournisseurs ne seront pas responsables des dommages indirects, accessoires, spéciaux, exemplaires ou consécutifs
          découlant de l'utilisation ou de l'impossibilité d'utiliser PROGÈRE, notamment&nbsp;: perte de profits, de revenus, d'occasion
          ou de contrat, retard de projet, perte de productivité, réclamation d'un tiers, décision prise à partir de données non
          vérifiées, erreur provenant des données du Client, ou utilisation non autorisée par un utilisateur du Client.
        </p>
        <p>
          Dans toute la mesure permise par les lois applicables, la responsabilité totale cumulative de Groupe MJR Construction inc.
          relativement à un événement ou à une série d'événements liés ne dépassera pas le montant des frais d'abonnement effectivement
          payés par le Client pour le module concerné au cours des douze mois précédant l'événement ayant donné lieu à la réclamation.
        </p>
        <p>
          Cette limitation ne s'applique pas dans les cas où son application est interdite par la loi, notamment lorsqu'une responsabilité
          ne peut pas être exclue ou limitée légalement, ni à une responsabilité découlant d'une faute intentionnelle ou d'une faute
          lourde.
        </p>
      </LegalSection>

      <LegalSection id="exclusions-garanties" title="23. Exclusions de garanties">
        <p>
          Dans toute la mesure permise par les lois applicables, PROGÈRE est fourni comme une plateforme technologique selon les
          fonctionnalités décrites dans le forfait applicable. Sauf garantie expressément prévue au contrat, PROGÈRE ne garantit pas qu'une
          fonctionnalité répondra à tous les besoins particuliers du Client, qu'une information saisie par le Client est exacte, qu'un
          calcul fondé sur une donnée erronée sera exact, qu'une intégration tierce sera toujours disponible, qu'un contenu généré par IA
          sera exempt d'erreur, qu'un résultat remplacera une validation professionnelle, ni qu'un service gouvernemental acceptera
          automatiquement un fichier. Aucune disposition ne supprime une garantie obligatoire qui ne peut pas être exclue par la loi.
        </p>
      </LegalSection>

      <LegalSection id="indemnisation" title="24. Indemnisation par le Client">
        <p>
          Dans toute la mesure permise par les lois applicables, le Client accepte de défendre, indemniser et tenir indemnes Groupe MJR
          Construction inc., ses administrateurs, dirigeants, employés et mandataires à l'égard de toute réclamation, perte, dommage ou
          dépense (y compris les honoraires raisonnables) découlant de&nbsp;: son utilisation de la plateforme, les données qu'il y place,
          le non-respect des présentes conditions, ou la violation des droits d'un tiers ou d'une loi applicable.
        </p>
      </LegalSection>

      <LegalSection id="revision-juridique" title="25. Avis">
        <p className="text-white/60 text-sm">
          Les présentes conditions doivent faire l'objet d'une révision par un avocat ou une avocate ayant une pratique au Québec avant le
          lancement public. « Dans toute la mesure permise par les lois applicables » signifie qu'aucune clause ne vise à écarter un droit
          impératif du consommateur ou une obligation légale d'ordre public.
        </p>
      </LegalSection>
    </LegalLayout>
  );
}
